import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';

import { Button } from '../components/ui';
import { Breadcrumb } from '../components/Breadcrumb';
import { LabelTypeSelector } from '../components/tools/LabelTypeSelector';
import { LabelParameterForm } from '../components/tools/LabelParameterForm';
import { LabelPreview } from '../components/tools/LabelPreview';
import { LabelPrinterStatus } from '../components/tools/LabelPrinterStatus';
import { PrinterSelector } from '../components/tools/PrinterSelector';
import { JobProgressOverlay } from '../components/tools/JobProgressOverlay';
import { apiService } from '../services/apiService';
import {
  BinLabelParameters,
  ExternalPartLabelParameters,
  InternalPartLabelParameters,
  ShippingLabelParameters,
  LabelParameters,
  LabelType,
  Printer as PrinterRecord,
} from '../services/types';

export const LabelPrintingPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [selectedLabelType, setSelectedLabelType] = useState<LabelType>(
    (searchParams.get('type') as LabelType) || LabelType.INTERNAL_PART
  );
  const [parameters, setParameters] = useState<LabelParameters>({
    partNumber: '',
  });
  const [quantity, setQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState('1');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [previewError, setPreviewError] = useState<string>('');
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Job progress state
  const [processingStatus, setProcessingStatus] = useState<
    'idle' | 'submitted' | 'processing' | 'completed' | 'failed'
  >('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [progressError, setProgressError] = useState<string | null>(null);
  const pollAbortControllerRef = useRef<AbortController | null>(null);
  const previewAbortControllerRef = useRef<AbortController | null>(null);
  // Start as true to prevent auto-preview; set to false when URL params are detected
  const hasAutoPreviewedRef = useRef(true);

  // Printer selection state
  const [printers, setPrinters] = useState<PrinterRecord[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(
    null
  );
  const [printersLoading, setPrintersLoading] = useState(true);
  const [printersError, setPrintersError] = useState<string | null>(null);

  // Load printers + last-used preference on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setPrintersLoading(true);
        setPrintersError(null);
        const [list, prefs] = await Promise.all([
          apiService.listPrinters(),
          apiService.getUserPreferences().catch(() => null),
        ]);
        if (cancelled) return;
        setPrinters(list);
        const enabled = list.filter((p) => p.enabled && p.kind === 'label');
        const fromPrefs = prefs?.lastPrinterId
          ? enabled.find((p) => p.printerId === prefs.lastPrinterId)
          : undefined;
        const initial = fromPrefs || enabled[0];
        if (initial) {
          setSelectedPrinterId(initial.printerId);
        }
      } catch (err) {
        if (cancelled) return;
        setPrintersError(
          err instanceof Error ? err.message : 'Failed to load printers'
        );
      } finally {
        if (!cancelled) setPrintersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePrinterChange = async (printerId: string) => {
    setSelectedPrinterId(printerId);
    // Persist server-side so the choice follows the user across devices.
    // Fire-and-forget: a failure here shouldn't block printing.
    apiService.setLastPrinterId(printerId).catch((err) => {
      console.warn('Failed to persist last printer:', err);
    });
  };

  // Update URL when label type changes
  useEffect(() => {
    setSearchParams({ type: selectedLabelType }, { replace: true });
  }, [selectedLabelType, setSearchParams]);

  // Handle label type change - reset quantity to 1
  const handleLabelTypeChange = (newType: LabelType) => {
    setSelectedLabelType(newType);
    setQuantity(1);
    setQuantityInput('1');
  };

  // Pre-populate parameters from URL
  useEffect(() => {
    const urlParams: Partial<LabelParameters> = {};
    let urlQuantity = 1;

    searchParams.forEach((value, key) => {
      if (key === 'type') {
        return;
      }

      if (key === 'quantity') {
        urlQuantity = parseInt(value, 10) || 1;
        return;
      }

      const paramKey = key as keyof LabelParameters;
      urlParams[paramKey] = value as LabelParameters[typeof paramKey];
    });

    if (Object.keys(urlParams).length > 0) {
      setParameters(urlParams as LabelParameters);
    }
    if (urlQuantity !== 1) {
      setQuantity(urlQuantity);
      setQuantityInput(String(urlQuantity));
    }

    // Mark that we should auto-preview if URL had relevant params
    if (Object.keys(urlParams).length > 0) {
      hasAutoPreviewedRef.current = false; // Reset to allow auto-preview
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-generate preview when parameters are loaded from URL
  useEffect(() => {
    // Only auto-preview once, when we have parameters from URL
    if (hasAutoPreviewedRef.current) return;

    // Check if we have the required parameter based on label type
    const hasRequiredParam =
      selectedLabelType === LabelType.INTERNAL_PART
        ? !!(parameters as InternalPartLabelParameters).partNumber
        : selectedLabelType === LabelType.EXTERNAL_PART
          ? !!(parameters as ExternalPartLabelParameters).part
          : selectedLabelType === LabelType.SHIPPING
            ? !!(parameters as ShippingLabelParameters).id
            : selectedLabelType === LabelType.BIN
              ? !!(parameters as BinLabelParameters).description
              : false;

    if (hasRequiredParam) {
      hasAutoPreviewedRef.current = true;

      // Create new abort controller for this request
      const abortController = new AbortController();
      previewAbortControllerRef.current = abortController;

      setIsLoadingPrint(true);
      generatePreviewImage(abortController).finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingPrint(false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters, selectedLabelType]);

  // Generate preview image via backend API
  const generatePreviewImage = async (
    abortController: AbortController
  ): Promise<void> => {
    try {
      setPreviewError('');

      // Call apiService to generate preview - handles auth and returns PNG blob
      const blob = await apiService.generatePrinterPreview(
        {
          labelType: selectedLabelType,
          parameters,
          quantity,
        },
        abortController.signal
      );

      // Only update state if not aborted
      if (!abortController.signal.aborted) {
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      }
    } catch (err) {
      // Ignore abort errors - they're intentional
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      // Only update error state if not aborted
      if (!abortController.signal.aborted) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setPreviewError(errorMessage);
        setImageUrl('');
      }
    }
  };

  const handlePreview = async () => {
    // Create new abort controller for this request
    const abortController = new AbortController();
    previewAbortControllerRef.current = abortController;

    setIsLoadingPrint(true);
    try {
      await generatePreviewImage(abortController);
    } finally {
      // Only clear loading if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoadingPrint(false);
      }
    }
  };

  const handlePrint = async () => {
    try {
      setError(null);
      setSuccess(null);
      setIsLoadingPrint(true);
      setProcessingStatus('submitted');
      setProgress(0);
      setProgressError(null);

      // Create abort controller for cancellable polling
      const abortController = new AbortController();
      pollAbortControllerRef.current = abortController;

      // Step 1: Submit job to printer via apiService (includes auth headers)
      // Backend will generate ZPL and queue the job
      const submitData = await apiService.submitPrinterJob({
        labelType: selectedLabelType,
        parameters,
        quantity,
        printerId: selectedPrinterId || undefined,
      });

      const currentJobId = submitData.jobId;
      setJobId(currentJobId);

      // Step 2: Poll for job status with progress updates
      await apiService.pollJobStatus(
        currentJobId,
        (statusResponse) => {
          if (statusResponse.jobProgress !== undefined) {
            setProgress(statusResponse.jobProgress);
          }
          if (statusResponse.status === 'PROCESSING') {
            setProcessingStatus('processing');
          } else if (statusResponse.status === 'COMPLETED') {
            setProcessingStatus('completed');
            setProgress(100);
          } else if (statusResponse.status === 'FAILED') {
            setProcessingStatus('failed');
          }
        },
        1000, // pollInterval: 1000ms
        120, // maxAttempts: 120 (2 minutes max)
        abortController.signal
      );

      // Step 3: Show success message
      setSuccess(
        t('labelPrinting.printSuccess', {
          count: quantity,
          jobId: currentJobId,
          defaultValue: `${quantity} label(s) printed successfully (Job ID: ${currentJobId})`,
        })
      );

      // Reset job state after 2 seconds
      setTimeout(() => {
        setProcessingStatus('idle');
        setJobId(null);
        setProgress(0);
      }, 2000);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';

      // Check if error is from cancellation
      if (errorMessage === 'Job polling cancelled by user') {
        setProgressError(
          t('labelPrinting.jobCancelled', {
            defaultValue: 'Print job cancelled',
          })
        );
      } else {
        setError(errorMessage);
        setProcessingStatus('failed');
        setProgressError(errorMessage);
      }
    } finally {
      setIsLoadingPrint(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Breadcrumb currentPage={t('labelPrinting.title')} />
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('labelPrinting.title', { defaultValue: 'Label Printing' })}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('labelPrinting.description', {
              defaultValue:
                'Select a label type and enter the details to print labels',
            })}
          </p>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Success Banner */}
        {success && (
          <div className="mb-6 rounded-lg bg-green-50 border border-green-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
              <button
                onClick={() => setSuccess(null)}
                className="ml-auto text-green-400 hover:text-green-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Configuration */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              {/* Printer Selector (first setting per multi-printer spec) */}
              <div>
                <PrinterSelector
                  printers={printers}
                  selectedPrinterId={selectedPrinterId}
                  onChange={handlePrinterChange}
                  isLoading={printersLoading}
                  error={printersError}
                />
              </div>

              {/* Label Type Selector */}
              <div className="border-t pt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('labelPrinting.selectLabelType', {
                    defaultValue: 'Label Type',
                  })}
                </h2>
                <LabelTypeSelector
                  selectedType={selectedLabelType}
                  onTypeChange={handleLabelTypeChange}
                />
              </div>

              {/* Printer Status */}
              <div className="border-t pt-6">
                <LabelPrinterStatus printerId={selectedPrinterId} />
              </div>

              {/* Quantity Input */}
              <div className="border-t pt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('labelPrinting.quantity', {
                    defaultValue: 'Number of Labels',
                  })}
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={quantityInput}
                  onKeyDown={(e) => {
                    // Block e, E, +, - characters (scientific notation)
                    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty for backspace
                    if (value === '') {
                      setQuantityInput('');
                      return;
                    }
                    const num = parseInt(value);
                    if (!isNaN(num)) {
                      // Clamp to max 100 immediately
                      const clamped = Math.min(100, num);
                      setQuantityInput(String(clamped));
                    }
                  }}
                  onBlur={() => {
                    const parsed = parseInt(quantityInput);
                    const validValue = isNaN(parsed) || parsed < 1 ? 1 : parsed;
                    setQuantity(validValue);
                    setQuantityInput(String(validValue));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t('labelPrinting.quantityHint', {
                    defaultValue: 'Between 1 and 100',
                  })}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="border-t pt-6 space-y-3">
                <Button
                  onClick={handlePrint}
                  disabled={
                    !selectedPrinterId ||
                    (selectedLabelType === LabelType.INTERNAL_PART
                      ? !(parameters as InternalPartLabelParameters).partNumber
                      : selectedLabelType === LabelType.EXTERNAL_PART
                        ? !(parameters as ExternalPartLabelParameters).part
                        : selectedLabelType === LabelType.SHIPPING
                          ? !(parameters as ShippingLabelParameters).id
                          : selectedLabelType === LabelType.BIN
                            ? !(parameters as BinLabelParameters).description
                            : true)
                  }
                  className="w-full gap-2"
                >
                  <Printer className="w-4 h-4" />
                  {t('labelPrinting.print', { defaultValue: 'Print' })}
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column: Form & Preview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Parameter Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('labelPrinting.parameters', {
                  defaultValue: 'Label Parameters',
                })}
              </h2>
              <LabelParameterForm
                labelType={selectedLabelType}
                parameters={parameters}
                onParametersChange={setParameters}
                onBlur={handlePreview}
              />
            </div>

            {/* Label Preview */}
            {imageUrl || previewError || isLoadingPrint ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('labelPrinting.preview', { defaultValue: 'Preview' })}
                </h2>
                <LabelPreview
                  imageUrl={imageUrl}
                  isLoading={isLoadingPrint}
                  error={previewError}
                />
              </div>
            ) : null}
          </div>
        </div>

        {/* Job Progress Overlay */}
        <JobProgressOverlay
          isVisible={
            processingStatus === 'submitted' ||
            processingStatus === 'processing' ||
            processingStatus === 'completed' ||
            processingStatus === 'failed'
          }
          progress={progress}
          jobId={jobId || undefined}
          message={t('labelPrinting.processingMessage', {
            defaultValue: 'Printing labels...',
          })}
          errorMessage={progressError || undefined}
          isComplete={processingStatus === 'completed'}
          onCancel={() => {
            if (pollAbortControllerRef.current) {
              pollAbortControllerRef.current.abort();
            }
            setProcessingStatus('idle');
            setProgress(0);
            setJobId(null);
            setProgressError(null);
          }}
        />
      </div>
    </div>
  );
};
